import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase.js';
import { useAuth } from '../auth/authStore.js';
import { MISSIONS_KEY } from '../missions/hooks.js';
import { usePendingQueueStore, type PendingCompletion } from './pendingQueueStore.js';

/**
 * Supabase-backed progress hooks — replaces the Phase 6 localStorage store.
 *
 * Source of truth is the database (via React Query cache). A mutation
 * that fails (offline, 5xx, RLS rejection) is pushed onto the pending
 * queue and retried when the user comes back online or focuses the tab.
 */

export interface CompletionRow {
  lesson_id: string;
  xp_earned: number;
  accuracy: number;
  perfect: boolean;
  completed_at: string;
}

export interface StreakRow {
  current_streak: number;
  longest_streak: number;
  freezes_available: number;
  last_active_date: string | null;
}

export interface CompleteLessonPayload {
  lessonId: string;
  xp: number;
  accuracy: number;
  perfect: boolean;
}

export interface CompleteLessonResult {
  xp_total: number;
  current_streak: number;
  longest_streak: number;
  freezes_available: number;
  last_active_date: string;
  timezone: string;
}

/* ---------------------------- queries ---------------------------- */

export const COMPLETIONS_KEY = (userId: string) => ['progress', 'completions', userId] as const;
export const STREAK_KEY = (userId: string) => ['progress', 'streak', userId] as const;
export const XP_TOTAL_KEY = (userId: string) => ['progress', 'xp_total', userId] as const;

async function fetchCompletions(userId: string): Promise<Record<string, CompletionRow>> {
  const { data, error } = await supabase
    .from('user_lesson_progress')
    .select('lesson_id, xp_earned, accuracy, perfect, completed_at')
    .eq('user_id', userId);
  if (error) throw error;
  const map: Record<string, CompletionRow> = {};
  for (const row of data ?? []) map[row.lesson_id] = row as CompletionRow;
  return map;
}

async function fetchStreak(userId: string): Promise<StreakRow> {
  const { data, error } = await supabase
    .from('user_streaks')
    .select('current_streak, longest_streak, freezes_available, last_active_date')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) throw error;
  return (
    data ?? {
      current_streak: 0,
      longest_streak: 0,
      freezes_available: 0,
      last_active_date: null,
    }
  );
}

async function fetchXpTotal(userId: string): Promise<number> {
  const { data, error } = await supabase
    .from('user_xp_events')
    .select('amount')
    .eq('user_id', userId);
  if (error) throw error;
  let total = 0;
  for (const row of data ?? []) total += row.amount ?? 0;
  return total;
}

/* --------- today in a user-local timezone (without a backend trip) --------- */
function todayInTz(tz: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const y = parts.find((p) => p.type === 'year')?.value ?? '1970';
  const m = parts.find((p) => p.type === 'month')?.value ?? '01';
  const d = parts.find((p) => p.type === 'day')?.value ?? '01';
  return `${y}-${m}-${d}`;
}

/* ---------------------------- hooks ---------------------------- */

export function useCompletions() {
  const { user } = useAuth();
  return useQuery({
    queryKey: user ? COMPLETIONS_KEY(user.id) : ['progress', 'completions', 'anon'],
    queryFn: () => fetchCompletions(user!.id),
    enabled: !!user,
    placeholderData: {},
  });
}

export function useStreak() {
  const { user } = useAuth();
  return useQuery({
    queryKey: user ? STREAK_KEY(user.id) : ['progress', 'streak', 'anon'],
    queryFn: () => fetchStreak(user!.id),
    enabled: !!user,
  });
}

/**
 * XP total + whether the query has *genuinely* resolved (vs. still showing the
 * `placeholderData: 0`). The `loaded` flag matters for level-up detection: a
 * consumer must not snapshot the pre-lesson XP baseline off the placeholder 0,
 * or every completion looks like a level-1→N jump and re-fires the LevelUpModal.
 */
export function useTotalXpQuery(): { total: number; loaded: boolean } {
  const { user } = useAuth();
  const { data, isPlaceholderData } = useQuery({
    queryKey: user ? XP_TOTAL_KEY(user.id) : ['progress', 'xp_total', 'anon'],
    queryFn: () => fetchXpTotal(user!.id),
    enabled: !!user,
    placeholderData: 0,
  });
  return { total: data ?? 0, loaded: !!user && !isPlaceholderData };
}

export function useTotalXp(): number {
  return useTotalXpQuery().total;
}

export function useCompletion(lessonId: string): CompletionRow | undefined {
  const { data } = useCompletions();
  return data?.[lessonId];
}

/* ------------------------- mutation ------------------------- */

async function callCompleteLesson(payload: CompleteLessonPayload): Promise<CompleteLessonResult> {
  const { data, error } = await supabase.rpc('complete_lesson', {
    p_lesson_id: payload.lessonId,
    p_xp: payload.xp,
    p_accuracy: payload.accuracy,
    p_perfect: payload.perfect,
  });
  if (error) throw error;
  return data as unknown as CompleteLessonResult;
}

function optimisticallyUpdateCaches(
  qc: QueryClient,
  userId: string,
  payload: CompleteLessonPayload,
  tz: string,
) {
  // Seed the completion row locally so the tree unlocks the next node
  // before the round-trip resolves.
  qc.setQueryData<Record<string, CompletionRow>>(COMPLETIONS_KEY(userId), (prev) => ({
    ...(prev ?? {}),
    [payload.lessonId]: {
      lesson_id: payload.lessonId,
      xp_earned: payload.xp,
      accuracy: payload.accuracy,
      perfect: payload.perfect,
      completed_at: new Date().toISOString(),
    },
  }));

  // Bump the cached XP total.
  qc.setQueryData<number>(XP_TOTAL_KEY(userId), (prev) => (prev ?? 0) + payload.xp);

  // Best-effort streak bump (matches the SQL rules; server response is canonical).
  qc.setQueryData<StreakRow>(STREAK_KEY(userId), (prev) => {
    const today = todayInTz(tz);
    if (!prev || prev.last_active_date === null) {
      return { current_streak: 1, longest_streak: 1, freezes_available: 0, last_active_date: today };
    }
    if (prev.last_active_date === today) return prev;
    // We can't compute yesterday accurately without knowing calendar math;
    // bump conservatively and let the server response correct us.
    const next = prev.current_streak + 1;
    return {
      current_streak: next,
      longest_streak: Math.max(prev.longest_streak, next),
      freezes_available: prev.freezes_available,
      last_active_date: today,
    };
  });
}

export function useCompleteLesson() {
  const qc = useQueryClient();
  const { user, profile } = useAuth();
  const enqueue = usePendingQueueStore((s) => s.enqueue);

  return useMutation<CompleteLessonResult, Error, CompleteLessonPayload>({
    mutationFn: callCompleteLesson,

    onMutate: (payload) => {
      if (!user) return;
      const tz = profile?.timezone ?? 'Asia/Tbilisi';
      optimisticallyUpdateCaches(qc, user.id, payload, tz);
      // Do NOT enqueue here — the PendingQueueFlusher would race and fire
      // a second RPC before this one resolves. Only enqueue on `onError`.
    },

    onSuccess: (result) => {
      if (!user) return;
      // Write the server's canonical totals into the streak + XP caches.
      qc.setQueryData<StreakRow>(STREAK_KEY(user.id), {
        current_streak: result.current_streak,
        longest_streak: result.longest_streak,
        freezes_available: result.freezes_available,
        last_active_date: result.last_active_date,
      });
      qc.setQueryData<number>(XP_TOTAL_KEY(user.id), result.xp_total);

      // Completions fetched fresh in the background so the row reflects
      // the DB's best-run policy (e.g. if a prior perfect run kept `perfect=true`).
      void qc.invalidateQueries({ queryKey: COMPLETIONS_KEY(user.id) });
      // Missions are recomputed server-side from the very tables this RPC just
      // wrote. Without this the card keeps its 2-minute-stale "0/1" while the
      // XP and the ring next to it already show the lesson — the daily-reward
      // moment lands on a card that says nothing happened.
      void qc.invalidateQueries({ queryKey: MISSIONS_KEY(user.id) });
    },

    onError: (_err, payload) => {
      if (!user) return;
      // Queue for retry by the flusher. Keyed so repeat failures don't pile up.
      const queued: PendingCompletion = {
        id: `${payload.lessonId}:${Date.now()}`,
        userId: user.id,
        payload,
        queuedAt: Date.now(),
      };
      enqueue('items', [queued]);
    },
  });
}

/* ------------------- top-level invalidator (used by retry loop) ------------------- */

export function invalidateProgress(qc: QueryClient, userId: string) {
  void qc.invalidateQueries({ queryKey: COMPLETIONS_KEY(userId) });
  void qc.invalidateQueries({ queryKey: STREAK_KEY(userId) });
  void qc.invalidateQueries({ queryKey: XP_TOTAL_KEY(userId) });
  // Missions live on the same source tables (lessons, practice sessions,
  // flashcard reviews, XP), so anything that moves progress moves missions.
  // Dropping it here means every flusher and every session-completion path
  // refreshes the card at once — the `useMissions` staleTime is 120s, long
  // enough that a returning-to-Home user would otherwise see yesterday's 0/1.
  void qc.invalidateQueries({ queryKey: MISSIONS_KEY(userId) });
}

/* ------------------- shared session-completion result ------------------- */

/**
 * The row shape every session-completion RPC returns
 * (`complete_flashcard_session`, `complete_practice_session` — same jsonb
 * contract as `complete_lesson`'s streak/XP fields). Owned here, next to the
 * caches it writes, so feature modules don't depend on each other for it.
 */
export interface SessionCompletionResult {
  xp_awarded: number;
  xp_total: number;
  current_streak: number;
  longest_streak: number;
  freezes_available: number;
  last_active_date: string;
  timezone: string;
  replayed: boolean;
}

/** Write a session-completion RPC result into the streak + XP caches. */
export function applySessionResultToCaches(
  qc: QueryClient,
  userId: string,
  result: SessionCompletionResult,
) {
  qc.setQueryData<StreakRow>(STREAK_KEY(userId), {
    current_streak: result.current_streak,
    longest_streak: result.longest_streak,
    freezes_available: result.freezes_available,
    last_active_date: result.last_active_date,
  });
  qc.setQueryData<number>(XP_TOTAL_KEY(userId), result.xp_total);
}

/** Direct RPC call without the React hooks — used by the retry flush loop. */
export async function completeLessonDirect(payload: CompleteLessonPayload): Promise<CompleteLessonResult> {
  return callCompleteLesson(payload);
}
